import React, { useRef, useEffect, useState } from "react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const Chart_Canvas_Heatmap_3_1_1 = ({
  dateSelected,
  pcListData, // Array of PC objects with their data
  // Format: [{ pc: "pc01", zone: "Zone A", remark: "VIP", data: [...sessions...] }]
}) => {
  // console.log(`### dateSelected`, dateSelected);
  // console.log(`### pcListData`, pcListData);

  const canvasRef = useRef(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    content: "",
  });
  const [hoveredRow, setHoveredRow] = useState(-1); // Track which row is hovered
  const [hoveredCol, setHoveredCol] = useState(-1); // Track which column is hovered

  // Constants for canvas layout
  const CELL_WIDTH = 13;
  const CELL_HEIGHT = 25; // Single row height
  const LABEL_WIDTH = 200;
  const HEADER_HEIGHT = 40;
  const TIME_BLOCKS = 96; // 24 hours * 4 (15min intervals)

  // Helper function to generate color from string (matching original)
  const stringToColor = (str) => {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) + hash + str.charCodeAt(i);
    }
    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = Math.abs((hash >> (i * 8)) & 0xff) >> 1;
      color += value.toString(16).padStart(2, "0");
    }
    return color;
  };

  useEffect(() => {
    if (!canvasRef.current || !pcListData || pcListData.length === 0) return;

    console.log("Canvas rendering with data:", pcListData.length, "PCs");
    console.log("Sample data:", pcListData[0]);
    console.log("Hovered row:", hoveredRow, "col:", hoveredCol);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Set canvas size
    const canvasWidth = LABEL_WIDTH + TIME_BLOCKS * CELL_WIDTH;
    const canvasHeight = HEADER_HEIGHT + pcListData.length * CELL_HEIGHT;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Generate time blocks
    const timeBlocks = [];
    const startTime = dayjs(`${dateSelected} 00:00`);
    for (let i = 0; i < TIME_BLOCKS; i++) {
      timeBlocks.push(startTime.add(i * 15, "minute"));
    }

    // Draw time header - ALL time blocks (00:00, 00:15, 00:30, ...)
    ctx.font = "9px monospace";
    timeBlocks.forEach((time, idx) => {
      const x = LABEL_WIDTH + idx * CELL_WIDTH;

      // Draw background for header cell
      ctx.fillStyle = "#eee";
      ctx.fillRect(x, 0, CELL_WIDTH - 1, HEADER_HEIGHT - 1);

      // Draw time text (rotated 90 degrees)
      ctx.fillStyle = "#000";
      ctx.save();
      ctx.translate(x + CELL_WIDTH / 2, HEADER_HEIGHT - 5);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(time.format("HH:mm"), 0, 0);
      ctx.restore();

      // Draw vertical lines at major time markers (every hour)
      if (time.minute() === 0) {
        ctx.strokeStyle = "#999";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, HEADER_HEIGHT);
        ctx.lineTo(x, canvasHeight);
        ctx.stroke();
      }
    });

    // Process and draw each PC row
    pcListData.forEach((pcInfo, rowIdx) => {
      const y = HEADER_HEIGHT + rowIdx * CELL_HEIGHT;

      // Draw row background with zone color (matching original)
      const rowColor = stringToColor(pcInfo.zone);
      ctx.fillStyle = rowColor;
      ctx.fillRect(0, y, LABEL_WIDTH, CELL_HEIGHT);

      // Draw everything in one row: 🔍 Zone  PC Name  Remark
      // Zone icon
      ctx.font = "12px Arial";
      ctx.fillText("🔍", 2, y + CELL_HEIGHT / 2 + 5);

      // Zone name
      ctx.fillStyle = "#FFF";
      ctx.font = "9px monospace";
      const zoneText =
        pcInfo.zone.length > 12
          ? pcInfo.zone.substring(0, 12) + "..."
          : pcInfo.zone;
      ctx.fillText(zoneText, 16, y + CELL_HEIGHT / 2 + 5);

      // PC name (bold)
      ctx.fillStyle = "#FFF";
      ctx.font = "bold 12px monospace";
      ctx.fillText(`${pcInfo.pc}`, 95, y + CELL_HEIGHT / 2 + 5);

      // Draw remark if exists with lime background (matching original)
      if (pcInfo.remark) {
        const remarkX = 145;
        const remarkWidth = 50;

        // Black background for remark
        ctx.fillStyle = "#000";
        ctx.fillRect(remarkX, y + 3, remarkWidth, CELL_HEIGHT - 6);

        // Lime text on black (truncated if too long)
        ctx.fillStyle = "#0f0";
        ctx.font = "9px monospace";
        const remarkText =
          pcInfo.remark.length > 6
            ? pcInfo.remark.substring(0, 6)
            : pcInfo.remark;
        ctx.fillText(`-${remarkText}`, remarkX + 2, y + CELL_HEIGHT / 2 + 4);
      }

      // Filter and parse session data for this PC
      const filteredRanges = (pcInfo.data || []).filter(
        (range) =>
          dayjs(range.dlsj).format("YYYY-MM-DD") === dateSelected ||
          dayjs(range.jssj).format("YYYY-MM-DD") === dateSelected
      );

      const parsedRanges = filteredRanges.map((range) => ({
        start: dayjs(range.dlsj),
        end: dayjs(range.jssj),
      }));

      // Draw time blocks for this PC
      timeBlocks.forEach((blockTime, colIdx) => {
        const x = LABEL_WIDTH + colIdx * CELL_WIDTH;

        // Check if this time block is in use
        const isInUse = parsedRanges.some(({ start, end }) => {
          return (
            blockTime.isSameOrAfter(start) && blockTime.isSameOrBefore(end)
          );
        });

        // Draw cell
        if (isInUse) {
          // Create gradient for active cells
          const gradient = ctx.createLinearGradient(x, y, x + CELL_WIDTH, y);
          gradient.addColorStop(0, "#ee0979");
          gradient.addColorStop(1, "#ff6a00");
          ctx.fillStyle = gradient;
        } else {
          ctx.fillStyle = "#eee";
        }

        ctx.fillRect(x, y, CELL_WIDTH - 1, CELL_HEIGHT - 1);
      });

      // Draw horizontal line after each row
      ctx.strokeStyle = "#ddd";
      ctx.beginPath();
      ctx.moveTo(0, y + CELL_HEIGHT);
      ctx.lineTo(canvasWidth, y + CELL_HEIGHT);
      ctx.stroke();
    });

    // Draw border
    ctx.strokeStyle = "#333";
    ctx.strokeRect(0, 0, canvasWidth, canvasHeight);

    // Draw highlight overlay on hovered column
    if (hoveredCol >= 0 && hoveredCol < TIME_BLOCKS) {
      const x = LABEL_WIDTH + hoveredCol * CELL_WIDTH;
      ctx.fillStyle = "rgba(173, 216, 230, 0.3)"; // Light blue overlay for column
      ctx.fillRect(x, HEADER_HEIGHT, CELL_WIDTH, canvasHeight - HEADER_HEIGHT);
    }

    // Draw highlight overlay on hovered row
    if (hoveredRow >= 0 && hoveredRow < pcListData.length) {
      const y = HEADER_HEIGHT + hoveredRow * CELL_HEIGHT;
      ctx.fillStyle = "rgba(255, 255, 255, 0.2)"; // Semi-transparent white overlay for row
      ctx.fillRect(0, y, canvasWidth, CELL_HEIGHT);

      // Draw row highlight border
      ctx.strokeStyle = "rgba(255, 255, 0, 0.8)"; // Yellow border
      ctx.lineWidth = 2;
      ctx.strokeRect(0, y, canvasWidth, CELL_HEIGHT);
    }

    // Draw crosshair highlight at intersection (if both row and col are hovered)
    if (
      hoveredRow >= 0 &&
      hoveredRow < pcListData.length &&
      hoveredCol >= 0 &&
      hoveredCol < TIME_BLOCKS
    ) {
      const x = LABEL_WIDTH + hoveredCol * CELL_WIDTH;
      const y = HEADER_HEIGHT + hoveredRow * CELL_HEIGHT;

      // Bright highlight at intersection
      ctx.fillStyle = "rgba(255, 255, 0, 0.4)"; // Bright yellow at intersection
      ctx.fillRect(x, y, CELL_WIDTH, CELL_HEIGHT);

      // Draw border around intersection cell
      ctx.strokeStyle = "rgba(255, 255, 0, 1)"; // Solid yellow border
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, CELL_WIDTH, CELL_HEIGHT);
    }
  }, [dateSelected, pcListData, hoveredRow, hoveredCol]);

  // Handle mouse move for tooltip and row/column highlighting
  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Track column position
    if (x > LABEL_WIDTH) {
      const colIdx = Math.floor((x - LABEL_WIDTH) / CELL_WIDTH);
      if (colIdx >= 0 && colIdx < TIME_BLOCKS) {
        setHoveredCol(colIdx);
      } else {
        setHoveredCol(-1);
      }
    } else {
      setHoveredCol(-1);
    }

    // Track row position
    if (y > HEADER_HEIGHT) {
      const rowIdx = Math.floor((y - HEADER_HEIGHT) / CELL_HEIGHT);

      if (rowIdx >= 0 && rowIdx < pcListData.length) {
        // Update hovered row
        setHoveredRow(rowIdx);

        // Show tooltip if over data cells
        if (x > LABEL_WIDTH) {
          const colIdx = Math.floor((x - LABEL_WIDTH) / CELL_WIDTH);
          if (colIdx >= 0 && colIdx < TIME_BLOCKS) {
            const pcInfo = pcListData[rowIdx];
            const startTime = dayjs(`${dateSelected} 00:00`).add(
              colIdx * 15,
              "minute"
            );

            setTooltip({
              visible: true,
              x: e.clientX,
              y: e.clientY,
              content: `${pcInfo.zone} , ${pcInfo.pc} , 📝${
                pcInfo.remark
              } , 🕰️${startTime.format("HH:mm")} `,
            });
          } else {
            setTooltip({ ...tooltip, visible: false });
          }
        } else {
          setTooltip({ ...tooltip, visible: false });
        }
      } else {
        setHoveredRow(-1);
        setTooltip({ ...tooltip, visible: false });
      }
    } else {
      setHoveredRow(-1);
      setTooltip({ ...tooltip, visible: false });
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ ...tooltip, visible: false });
    setHoveredRow(-1); // Clear row highlight when mouse leaves canvas
    setHoveredCol(-1); // Clear column highlight when mouse leaves canvas
  };

  return (
    <div style={{ position: "relative", overflow: "auto" }}>
      <canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          border: "1px solid #ccc",
          cursor: "crosshair",
          display: "block",
        }}
      />
      {tooltip.visible && (
        <div
          style={{
            position: "fixed",
            left: tooltip.x + 10,
            top: tooltip.y + 10,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            color: "white",
            padding: "5px 10px",
            borderRadius: "4px",
            fontSize: "12px",
            pointerEvents: "none",
            zIndex: 1000,
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};

export default Chart_Canvas_Heatmap_3_1_1;
